export const PROJECT_AI_ALLOWED_FIELDS = Object.freeze([
    'nome',
    'nivel',
    'finalidade',
    'area',
    'setor_id',
    'setorDemanda_id',
    'setores_envolvidos',
    'dtInicio',
    'dtEstimadaFinal',
    'gerentePCM_id',
    'objetivo_geral',
    'descricao_problema',
    'objetivos_estrategicos',
    'ganho_operacional_esperado',
    'escopo',
    'resultados',
    'impacto_viabilidade',
    'custo_justificativa',
])

const ID_FIELDS = new Set(['setor_id', 'setorDemanda_id', 'gerentePCM_id'])
const DATE_FIELDS = new Set(['dtInicio', 'dtEstimadaFinal'])
const ENUM_FIELDS = {
    nivel: ['Estratégico', 'Operacional'],
    finalidade: ['Criação', 'Mudança'],
    area: ['Processo', 'Produto'],
}

export const PROJECT_AI_STRATEGIC_OBJECTIVES = Object.freeze([
    { id: 1, nome: 'Desenvolver produtos inovadores' },
    { id: 2, nome: 'Melhoria do processo produtivo' },
    { id: 3, nome: 'Atender novos mercados' },
    { id: 4, nome: 'Liderança e celebração de resultados' },
    { id: 5, nome: 'Ampliação do projeto social para transformar vidas' },
    { id: 6, nome: 'Aumentar receitas de vendas' },
    { id: 7, nome: 'Assegurar a rentabilidade do negócio com critérios ASG' },
    { id: 8, nome: 'Assegurar a satisfação do cliente' },
    { id: 9, nome: 'Garantir a qualidade com sustentabilidade' },
    { id: 10, nome: 'Produzir de forma eficiente, reduzindo resíduos e incentivando a reutilização' },
    { id: 11, nome: 'Fortalecer o espírito de equipe e praticar a meritocracia' },
    { id: 12, nome: 'Aumentar a competência e engajamento do time' },
])

export const PROJECT_AI_IMPACT_QUESTIONS = Object.freeze([
    {
        impactoViabilidade_id: 1,
        descricao: 'Afetará a documentação de produtos já existentes? (B.O.M, Roteiro de Produção, Esquema elétrico, Estrutura, etc.)',
    },
    {
        impactoViabilidade_id: 6,
        descricao: 'O produto a ser alterado/criado possui ou deve possuir certificação?',
    },
])

function isMissing(value) {
    return value === undefined
        || value === null
        || (typeof value === 'string' && value.trim() === '')
}

function isEmptyPcmValue(value) {
    return isMissing(value)
}

function draftField(field) {
    if (!field || typeof field !== 'object' || Array.isArray(field) || !Object.prototype.hasOwnProperty.call(field, 'value')) {
        return { value: field, source: null, state: null }
    }

    return {
        value: field.value,
        source: field.source ?? null,
        state: field.state ?? null,
    }
}

export function monetaryAmount(value) {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null
    }
    if (typeof value !== 'string' || !value.trim()) return null

    let normalized = value.trim().replace(/[^\d,.-]/g, '')
    if (normalized.includes(',') && normalized.includes('.')) {
        normalized = normalized.replace(/\./g, '').replace(',', '.')
    } else if (normalized.includes(',')) {
        normalized = normalized.replace(',', '.')
    }
    const amount = Number(normalized)
    return Number.isFinite(amount) ? amount : null
}

export function hasMeaningfulResultContent(resultado) {
    if (!resultado || typeof resultado !== 'object') return false

    const cost = monetaryAmount(resultado.custo)
    const status = typeof resultado.status === 'string' ? resultado.status.trim().toLowerCase() : ''

    return !isMissing(resultado.nome)
        || !isMissing(resultado.problema)
        || !isMissing(resultado.descricao)
        || !isMissing(resultado.resultado_esperado)
        || !isMissing(resultado.evidencias)
        || !isMissing(resultado.evidencia)
        || !isMissing(resultado.dt_prevista_conclusao)
        || !isMissing(resultado.dt_conclusao)
        || cost !== null && Math.abs(cost) > 0
        || resultado.sem_custo === true
        || status !== '' && status !== 'pendente'
}

function hasManualResults(resultados) {
    if (!Array.isArray(resultados)) return false

    return resultados.some(hasMeaningfulResultContent)
}

function localResultId(value) {
    if (typeof value === 'string' && value.trim()) return value
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (token) => {
        const random = Math.floor(Math.random() * 16)
        return (token === 'x' ? random : (random & 0x3) | 0x8).toString(16)
    })
}

function normalizeResult(resultado, indice) {
    if (!resultado || typeof resultado !== 'object'
        || isMissing(resultado.nome)
        || isMissing(resultado.problema)
        || isMissing(resultado.descricao)
        || isMissing(resultado.evidencias)) {
        return null
    }

    const semCusto = resultado.sem_custo === true
    const custo = !semCusto && typeof resultado.custo === 'number'
        && Number.isFinite(resultado.custo) && resultado.custo >= 0
        ? resultado.custo
        : null
    const costState = ['known', 'estimated', 'no_cost', 'pending'].includes(resultado.cost_state)
        ? resultado.cost_state
        : semCusto
            ? 'no_cost'
            : custo !== null
                ? 'known'
                : 'pending'

    return {
        local_id: localResultId(resultado.local_id),
        nome: String(resultado.nome).trim(),
        problema: String(resultado.problema).trim(),
        descricao: String(resultado.descricao).trim(),
        evidencias: isMissing(resultado.evidencias) ? '' : String(resultado.evidencias).trim(),
        dt_prevista_conclusao: isMissing(resultado.dt_prevista_conclusao)
            ? null
            : String(resultado.dt_prevista_conclusao).slice(0, 10),
        dt_conclusao: null,
        sem_custo: semCusto,
        custo,
        cost_state: costState,
        cost_estimate: resultado.cost_estimate && typeof resultado.cost_estimate === 'object'
            ? { ...resultado.cost_estimate }
            : null,
        status: 'pendente',
        indice,
    }
}

function hasManualStrategicObjectives(value) {
    return Array.isArray(value) && value.some((item) => item?.ativo === true || item?.ativo === 1 || item?.ativo === '1')
}

function normalizeStrategicObjectives(value, current = null) {
    if (!Array.isArray(value)) return null

    const selected = new Map()
    value.forEach((item) => {
        if (!item || !Number.isInteger(item.objetivo_id)) return
        selected.set(item.objetivo_id, item.justificativa ?? null)
    })

    const options = Array.isArray(current) && current.length
        ? current
        : PROJECT_AI_STRATEGIC_OBJECTIVES

    return options.map((item) => ({
        ...item,
        ativo: selected.has(item.id) ? 1 : 0,
        justificativa: selected.has(item.id) ? selected.get(item.id) : (item.justificativa ?? null),
    }))
}

function hasManualImpactAnalysis(value) {
    return Array.isArray(value) && value.some((item) =>
        typeof item?.concordo === 'boolean' || !isMissing(item?.justificativa)
    )
}

function normalizeImpactAnalysis(value, current = null) {
    if (!Array.isArray(value)) return null

    const suggestions = new Map()
    value.forEach((item) => {
        if (!item || !Number.isInteger(item.impactoViabilidade_id)) return
        suggestions.set(item.impactoViabilidade_id, item)
    })
    const options = Array.isArray(current) && current.length
        ? current
        : PROJECT_AI_IMPACT_QUESTIONS

    return options.map((item) => {
        const suggestion = suggestions.get(item.impactoViabilidade_id)
        return {
            ...item,
            concordo: typeof suggestion?.concordo === 'boolean' ? suggestion.concordo : null,
            justificativa: isMissing(suggestion?.justificativa) ? '' : String(suggestion.justificativa).trim(),
            impact_state: ['yes', 'no', 'pending'].includes(suggestion?.impact_state)
                ? suggestion.impact_state
                : null,
            impact_reason: isMissing(suggestion?.reason ?? suggestion?.impact_reason)
                ? ''
                : String(suggestion.reason ?? suggestion.impact_reason).trim(),
            impact_confidence: typeof suggestion?.confidence === 'number' ? suggestion.confidence : null,
            impact_requires_confirmation: suggestion?.requires_confirmation === true,
        }
    })
}

function normalizeSetoresEnvolvidos(value) {
    if (!Array.isArray(value)) return null

    return value
        .filter((item) => item && Number.isInteger(item.setor_id))
        .map((item) => ({
            id: item.setor_id,
            justificativa: isMissing(item.justificativa) ? '' : String(item.justificativa).trim(),
        }))
}

function normalizeField(field, value) {
    if (ID_FIELDS.has(field)) {
        return typeof value === 'number' && Number.isFinite(value) ? value : undefined
    }

    if (ENUM_FIELDS[field]) {
        return typeof value === 'string' && ENUM_FIELDS[field].includes(value)
            ? value
            : undefined
    }

    if (DATE_FIELDS.has(field)) {
        return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
            ? value
            : undefined
    }

    if (typeof value === 'string') return value.trim()
    if (typeof value === 'number' || typeof value === 'boolean') return value
    return undefined
}

/**
 * Aplica somente sugestões permitidas em campos ainda vazios do mesmo objeto PCM.
 * A função não persiste nem cria projetos.
 */
export function applyProjectDraftToPcm(draft, pcm) {
    const report = {
        applied: [],
        conflicts: [],
        skipped: [],
    }

    if (!draft || typeof draft !== 'object' || !pcm || typeof pcm !== 'object') {
        report.skipped.push({ field: 'draft', reason: 'Rascunho inválido.' })
        return report
    }

    PROJECT_AI_ALLOWED_FIELDS.forEach((field) => {
        const draftItem = draftField(draft[field])
        const value = draftItem.value

        if (isMissing(value) || draftItem.state === 'missing' || draftItem.source === 'missing') {
            report.skipped.push({ field, reason: 'Valor não informado pela IA.' })
            return
        }

        if (field === 'resultados') {
            if (!Array.isArray(value)) {
                report.skipped.push({ field, reason: 'Formato de resultados inválido.' })
                return
            }
            if (hasManualResults(pcm.resultados)) {
                report.conflicts.push({
                    field,
                    current: pcm.resultados,
                    suggested: value,
                    reason: 'A lista atual possui conteúdo manual.',
                })
                return
            }

            const normalized = value
                .map(normalizeResult)
                .filter(Boolean)
                .map((resultado, indice) => ({ ...resultado, indice }))

            if (!normalized.length) {
                report.skipped.push({ field, reason: 'Nenhum resultado válido foi informado.' })
                return
            }

            pcm.resultados = normalized
            report.applied.push(field)
            return
        }

        if (field === 'objetivos_estrategicos') {
            if (hasManualStrategicObjectives(pcm.objetivos_estrategicos)) {
                report.conflicts.push({
                    field,
                    current: pcm.objetivos_estrategicos,
                    suggested: value,
                    reason: 'Já existem objetivos estratégicos selecionados manualmente.',
                })
                return
            }
            const normalized = normalizeStrategicObjectives(value, pcm.objetivos_estrategicos)
            if (!normalized) {
                report.skipped.push({ field, reason: 'Formato de objetivos estratégicos inválido.' })
                return
            }
            pcm.objetivos_estrategicos = normalized
            report.applied.push(field)
            return
        }

        if (field === 'impacto_viabilidade') {
            if (hasManualImpactAnalysis(pcm.impacto_viabilidade)) {
                report.conflicts.push({
                    field,
                    current: pcm.impacto_viabilidade,
                    suggested: value,
                    reason: 'Já existem respostas manuais de impacto e viabilidade.',
                })
                return
            }
            const normalized = normalizeImpactAnalysis(value, pcm.impacto_viabilidade)
            if (!normalized) {
                report.skipped.push({ field, reason: 'Formato da análise de impactos inválido.' })
                return
            }
            pcm.impacto_viabilidade = normalized
            report.applied.push(field)
            return
        }

        if (field === 'setores_envolvidos') {
            if (Array.isArray(pcm.setores_envolvidos) && pcm.setores_envolvidos.length) {
                report.conflicts.push({
                    field,
                    current: pcm.setores_envolvidos,
                    suggested: value,
                    reason: 'Já existem setores envolvidos informados manualmente.',
                })
                return
            }
            const normalized = normalizeSetoresEnvolvidos(value)
            if (!normalized) {
                report.skipped.push({ field, reason: 'Formato de setores envolvidos inválido.' })
                return
            }
            pcm.setores_envolvidos = normalized
            report.applied.push(field)
            return
        }

        const normalized = normalizeField(field, value)
        if (normalized === undefined) {
            report.skipped.push({
                field,
                reason: ID_FIELDS.has(field)
                    ? 'O identificador precisa ser numérico.'
                    : 'Formato não suportado.',
            })
            return
        }

        if (!isEmptyPcmValue(pcm[field])) {
            report.conflicts.push({
                field,
                current: pcm[field],
                suggested: normalized,
                reason: 'O campo já possui conteúdo.',
            })
            return
        }

        pcm[field] = normalized
        report.applied.push(field)
    })

    return report
}
